import { useRef, useCallback, TouchEvent } from "react";

interface SwipeGestureConfig {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number; // Minimum distance to trigger swipe
  velocityThreshold?: number; // Minimum velocity for quick swipes
}

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
}

export function useSwipeGesture(config: SwipeGestureConfig) {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    velocityThreshold = 0.3,
  } = config;

  const swipeState = useRef<SwipeState | null>(null);

  const onTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    swipeState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      currentX: touch.clientX,
      currentY: touch.clientY,
    };
  }, []);

  const onTouchMove = useCallback((e: TouchEvent) => {
    if (!swipeState.current) return;
    
    const touch = e.touches[0];
    swipeState.current.currentX = touch.clientX;
    swipeState.current.currentY = touch.clientY;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!swipeState.current) return;

    const { startX, startY, startTime, currentX, currentY } = swipeState.current;
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const deltaTime = Date.now() - startTime;
    
    // Calculate velocity (pixels per millisecond)
    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Determine if it's a horizontal or vertical swipe
    const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
    
    // Check if swipe meets threshold or velocity requirements
    const meetsHorizontalThreshold = Math.abs(deltaX) > threshold || velocityX > velocityThreshold;
    const meetsVerticalThreshold = Math.abs(deltaY) > threshold || velocityY > velocityThreshold;

    if (isHorizontal && meetsHorizontalThreshold) {
      if (deltaX > 0) {
        onSwipeRight?.();
      } else {
        onSwipeLeft?.();
      }
    } else if (!isHorizontal && meetsVerticalThreshold) {
      if (deltaY > 0) {
        onSwipeDown?.();
      } else {
        onSwipeUp?.();
      }
    }

    swipeState.current = null;
  }, [onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown, threshold, velocityThreshold]);

  // Calculate current drag offset for visual feedback
  const getDragOffset = useCallback(() => {
    if (!swipeState.current) return { x: 0, y: 0 };
    return {
      x: swipeState.current.currentX - swipeState.current.startX,
      y: swipeState.current.currentY - swipeState.current.startY,
    };
  }, []);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    getDragOffset,
  };
}
